import { CONFIG } from "~/config";
import { IMapset } from "~/interfaces";
import SceneLayerTraffic from "~/scene/layer/traffic";
import SceneLayerWinter from "~/scene/layer/winter";
import SceneLayerTile from "~/scene/layer/tile";
import SceneLayerTurist from "~/scene/layer/turist";
import Scene from "~/scene";
import { checkOphotoDate } from "~/util/ophotoDate";
import { SATURATION_MAPSETS } from "~/util/constants";
import { watch, watchMapset, watchCoordinates } from "~/state/util";
import { store } from "~/state/store";
import { Unsubscribe } from "redux";
import MapsetOverlays from "~/scene/mapsetoverlays";
import { MAP_OVERLAYS } from "~/state/map/constants";
import { IMapState } from "~/state/map/interfaces";

/**
 * Sprava mapovych vrstev definovanych v jednotlivych mapsetech + nucene podkladove vrstvy
 */
const RETINA = {
	"winter-en": "winter-en/retina",
};

type LayerName = "base-dark" | "base" | "ophoto" | "turist" | "ophoto0203" | "ophoto0406" | "ophoto1012" | "ophoto1415" | "ophoto1618" | "hybrid" | "trail" | "bike" | "history" | "summer" | "geography";

interface ILayerData {
	traffic: {
		normalBase: string;
		normalUp: string;
		traffic: string;
		ophotoDown: string;
		ophotoUp: string;
	};
	winter: {
		normalBase: string;
		normalUp: string;
		normalDown: string;
		winter: string;
		winterOphotoUp: string;
		winterOphotoUpName: string;
		winterOphotoDown: string;
		winterOphotoDownName: string;
	};
}

interface ILayerOptions {
	animTime: number;
	fadeTime?: number;
	prefetch: number;
	alpha: boolean;
	retinaUrl?: string;
}

function defToString(def: string | {
	[key: string]: any;
}) {
	if (typeof def === "string") {
		return def;
	}

	return JSON.stringify(def);
}

function getMapsetData(name: string): {
	copyright: Array<Array<string>>,
	config: IMapset,
} {
	const config: IMapset = Mapy.Util.getMapset(name);

	if (config) {
		let copyright = null;

		if (config.copyright && config.copyright.length) {
			const curYear = new Date().getFullYear().toString();

			copyright = {};

			config.copyright.forEach(item => {
				// odkazy a rok
				if (!copyright[item[0]]) {
					copyright[item[0]] = [];
				}
				copyright[item[0]].push(item[1].replace(/"/gu, "'").replace(/%YEAR%/gu, curYear));
			});
		}

		return {
			config,
			copyright,
		};
	}

	return null;
}

function getLayerData(): ILayerData {
	const lang = CONFIG.defaultLang;
	const traffic = {
		normalBase: "base-m-traf-down",
		normalUp: "base-m-traf-up",
		traffic: "traffic",
		ophotoDown: "hybrid-base-m-traf-down",
		ophotoUp: "hybrid-base-m-traf-up",
	};
	const winter = {
		normalBase: "winter-m-down",
		normalUp: "winter-m-up",
		normalDown: "winter-m-down",
		winter: "winter",
		winterOphotoUp: "hybrid-base-m-traf-up",
		winterOphotoUpName: "hybrid-base-m-traf-up-winter",
		winterOphotoDown: "hybrid-base-m-traf-down",
		winterOphotoDownName: "hybrid-base-m-traf-down-winter",
	};

	if (lang !== "cs" && lang !== "sk") {
		traffic.normalBase = "base-en-traf-down";
		traffic.normalUp = "base-en-traf-up";
		traffic.ophotoDown = "hybrid-base-en-traf-down";
		traffic.ophotoUp = "hybrid-base-en-traf-up";

		winter.normalBase = "winter-en-down";
		winter.normalUp = "winter-en-up";
		winter.normalDown = "winter-en-down";
		winter.winter = "winter-en";
		winter.winterOphotoUp = "hybrid-base-en-traf-up";
		winter.winterOphotoDown = "hybrid-base-en-traf-down";
	}

	return {
		traffic,
		winter,
	};
}

export default class LayerManager {
	_nameToId: {
		[key in LayerName]: number;
	};

	_trafficConst: ILayerData["traffic"];

	_winterConst: ILayerData["winter"];

	_trafficLayer: SceneLayerTraffic;

	_winterLayer: SceneLayerWinter;

	_layers: {
		[key: string]: any;
	};

	_active: {
		[key: string]: any;
	};

	_zooms: {
		[key: string]: [number, number];
	};

	_ss: Array<string>;

	_ophotoLayers: Array<any>;

	_sets: {
		[key: string]: number;
	};

	_invertMapsets: Array<string>;

	_ophoto: {
		container: HTMLDivElement;
		start: () => void;
		stop: () => void;
	};

	_ophotoDateCheck: boolean;

	_loadPromise: Promise<typeof import("~/ui/ophoto-timeline/index")>;

	_mapsetOverlays: MapsetOverlays;

	_storeMapsetUnsubscribe: Unsubscribe;

	_storeCoordinatesUnsubscribe: Unsubscribe;

	constructor() {
		const layerData = getLayerData();

		this._trafficConst = layerData.traffic;
		this._winterConst = layerData.winter;
		this._nameToId = {
			"base-dark": Scene.MAPSET.BASE,
			"base": Scene.MAPSET.BASE,
			"ophoto": Scene.MAPSET.OPHOTO,
			"turist": Scene.MAPSET.TURIST,
			"ophoto0203": Scene.MAPSET.OPHOTO0203,
			"ophoto0406": Scene.MAPSET.OPHOTO0406,
			"ophoto1012": Scene.MAPSET.OPHOTO1012,
			"ophoto1415": Scene.MAPSET.OPHOTO1415,
			"ophoto1618": Scene.MAPSET.OPHOTO1618,
			"hybrid": Scene.MAPSET.HYBRID,
			"trail": Scene.MAPSET.TRAIL,
			"bike": Scene.MAPSET.BIKE,
			"history": Scene.MAPSET.HISTORIC,
			"summer": Scene.MAPSET.SUMMER,
			"geography": Scene.MAPSET.GEOGRAPHY,
		};
		this._trafficLayer = Scene.createLayerTraffic("layer-traffic", {
			typeIds: [2010],
		});
		this._winterLayer = Scene.createLayerWinter("layer-winter", {
			typeIds: [1046, 1047, 1048],
			mouseReactExtension: 4,
		});

		this._layers = {}; /* cache vrstev */
		this._layers[this._trafficConst.traffic] = this._trafficLayer;
		this._layers[this._winterConst.winter] = this._winterLayer;
		this._active = {}; /* prave zobrazene */
		this._zooms = {}; /* ulozene zoomy pro dynamicke ID */
		this._ss = [];
		this._ophotoLayers = [];
		this._sets = {};
		this._ophotoDateCheck = false;
		this._invertMapsets = ["kn"];

		for (let index = 0; index < CONFIG.mapsets.length; index++) {
			if (!CONFIG.mapsets[index].seo) {
				continue;
			}

			this._sets[CONFIG.mapsets[index].seo] = index;
		}

		// traffic vrstvy - vytvorime je pro pozdejsi pouziti
		this._addExtraLayer(this._trafficConst.normalBase, "dopravni", [2, 19], true);
		this._addExtraLayer(this._trafficConst.ophotoDown, "dopravni");
		this._addExtraLayer(this._trafficConst.normalUp, "dopravni");
		this._addExtraLayer(this._trafficConst.ophotoUp, "dopravni");
		this._addExtraLayer(this._winterConst.normalBase, "zimni", [2, 19], true);
		this._addExtraLayer(this._winterConst.normalUp, "zimni", [2, 19], true);
		this._addCustomLayer(this._winterConst.winterOphotoUp, this._winterConst.winterOphotoUpName);
		this._addCustomLayer(this._winterConst.winterOphotoDown, this._winterConst.winterOphotoDownName);

		Scene.addListener("provider-changed", data => {
			if (data.data.name === "map") {
				const sMap = Scene.providers.map.getMap();
				const { mapset } = store.getState().map;
				const saturation = SATURATION_MAPSETS.indexOf(mapset) > -1;

				sMap.getLayerContainer(SMap.LAYER_TILE).classList.add("tiles");
				sMap.getLayerContainer(SMap.LAYER_TILE).classList[saturation ? "add" : "remove"]("saturationOn");
			}
		});

		// data pro mapsety
		const mapsets = CONFIG.mapsets.filter(item => item.seo.indexOf("letecka") !== -1 && !item.hidden).map(item => ({
			caption: item.seo === "letecka" ? "Nejnovější" : item.name,
			seo: item.seo,
			year: item.seo === "letecka" ? new Date().getFullYear() : parseFloat(item.seo.replace("letecka-", "")),
		}));

		mapsets.reverse();

		for (let ind = 0, max = mapsets.length - 1; ind < max; ind++) {
			mapsets[ind].caption = `${mapsets[ind].year}-${mapsets[ind + 1].year - 1}`;
		}

		mapsets.reverse();

		const wtMapset = watch(store.getState, "map", watchMapset);
		const wtCoordinates = watch(store.getState, "map", watchCoordinates);

		this._storeMapsetUnsubscribe = store.subscribe(wtMapset((newMap, oldMap) => {
			if (newMap.provider === "m3d") {
				this._removeOphotoTimeline();
				Scene.setMapset3D(newMap.mapset);
			} else if (newMap.provider === "wasm") {
				this._removeOphotoTimeline();
				Scene.setMapsetWasm(newMap.mapset);
			} else if ((!oldMap.mapset || newMap.mapset !== oldMap.mapset) || newMap.provider !== oldMap.provider) {
				this.setMapset(newMap.mapset);
			}

			this._updateOverlays(newMap, oldMap);
		}));
		this._storeCoordinatesUnsubscribe = store.subscribe(wtCoordinates((newMap, oldMap) => {
			if (
				newMap.longitude !== oldMap.longitude
				|| newMap.latitude !== oldMap.latitude
				|| newMap.zoom !== oldMap.zoom
			) {
				const { longitude, latitude, zoom } = newMap;

				this._mapRedraw({ longitude, latitude, zoom });
			}
		}));

		const state = store.getState().map;

		this._mapsetOverlays = new MapsetOverlays();
		this.setMapset(state.mapset);
		this._updateOverlays(state);
	}

	$destructor() {
		Scene.removeListeners(this._ss);
		this._mapsetOverlays?.destroy();
	}

	_updateOverlays(newMapState: IMapState, oldMapState?: IMapState) {
		const overlaysToLayers = {
			[MAP_OVERLAYS.TRAFFIC]: {
				layer: this._trafficLayer,
			},
			[MAP_OVERLAYS.SKI]: {
				layer: this._winterLayer,
			},
		};

		Object.keys(overlaysToLayers).map(overlay => parseInt(overlay)).forEach(overlay => {
			const oldOverlay = oldMapState ? oldMapState.mapset in oldMapState.currentOverlays && oldMapState.currentOverlays[oldMapState.mapset].indexOf(overlay) !== -1 : false;
			const newOverlay = newMapState.mapset in newMapState.currentOverlays && newMapState.currentOverlays[newMapState.mapset].indexOf(overlay) !== -1;

			if (oldOverlay !== newOverlay) {
				if (newOverlay) {
					overlaysToLayers[overlay.toString()].layer.enable();
				} else {
					overlaysToLayers[overlay.toString()].layer.disable();
				}
			}
		});

		if (["dopravni", "dopravni-zpravy", "uzavirky"].indexOf(newMapState.mapset) !== -1) {
			this._trafficLayer.enable();
		}
	}

	getTrafficLayer() {
		return this._trafficLayer;
	}

	getWinterLayer() {
		return this._winterLayer;
	}

	getLayerId(name: string, isBase: boolean): number {
		/* vraci id podle jmena vrstvy v this._nameToId */
		if (name in this._nameToId) {
			return this._nameToId[name];
		}
		/* pokud jde o zakladni vrstvu a neni nalezena, vraci 'base' vrstvu*/
		if (isBase) {
			return this._nameToId.base;
		}

		/* jinak nevrati nic*/
		return null;
	}

	isTrafficActive() {
		return this._trafficLayer.isActive();
	}

	setMapset(seo: string | {
		[key: string]: any;
	}) {
		const { longitude, latitude, zoom } = store.getState().map;

		if (Scene.isMapActive() && typeof seo === "string") {
			const sMap = Scene.providers.map.getMap();
			const saturation = SATURATION_MAPSETS.indexOf(seo) > -1;

			sMap.getLayerContainer(SMap.LAYER_TILE).classList[saturation ? "add" : "remove"]("saturationOn");
		}

		if (seo === "dopravni" || seo === "dopravni-zpravy" || seo === "uzavirky") {
			checkOphotoDate();
			this._setMapsetTraffic({ longitude, latitude, zoom });

			this._removeOphotoTimeline();

			return;
		}

		if (seo === "zimni") {
			checkOphotoDate();
			this._setMapsetWinter({ longitude, latitude, zoom });
			this._removeOphotoTimeline();

			return;
		}

		const def = (typeof seo === "string" ? CONFIG.mapsets[this._sets[seo]] : seo) || CONFIG.mapsets[this._sets.zakladni];
		const reused = {};

		/**
		 * Info:
		 * - this._addLayer nyni nepovoluje vrstvu, ceka se na upravu zoomu
		 * - this._syncZoomRange(base.getId());
		 * - po synchronizaci zoomu se vsechny aktivni vrstvy povoli
		 * Bugfix: zakladni mapa, zoom 18, dame leteckou, zoom 20, prepneme zpet na zakladni,
		 *         vyvola se asi 30 requestu 404 - protoze na zoomu 20 neni podklad
		 */

		/* pridat zaklad, podle nej se rozhodovat */
		const base = this._addLayer(def.base, reused, true);

		/* projit dalsi a volitelne pridavat/reusovat */
		const layers: Array<string> = def.layers.slice();

		if (typeof seo !== "string" && layers.indexOf("hybrid") === -1 && layers.indexOf("trail") === -1) {
			layers.push("hybrid");
		}

		if (def.base === "turist" && base instanceof SceneLayerTurist) {
			base.setBike(false);
			base.setTrail(false);
		}

		this._addLayer(this._trafficConst.traffic, reused);
		this._mapsetOverlays?.bumpLayers("middle");

		/* pridat dodatecne vrstvy */
		for (let index = 0; index < layers.length; index++) {
			const id = layers[index];

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			if (def.base === "turist" && base instanceof SceneLayerTurist) {
				switch (id) {
					case "bike":
						base.setBike(true);
						break;
					case "trail":
						base.setTrail(true);
						break;
					case "hybrid":
						this._addLayer("hybrid", reused);
						break;
					default:
						break;
				}
			} else {
				this._addAdditionalLayer(id, reused);
			}
		}

		// fix kvuli spravnemu poradi vrstev na hybridu (napr. turist vs ophoto vs pano)
		if (layers.length) {
			this._mapsetOverlays?.bumpLayers("top");
		}

		/* odebrat nepouzite */
		this._removeLayers(reused);

		/* nastavit rozsah zoomu, je-li */
		this._syncZoomRange(base.getId());

		/* aktivni vrstvy povolime */
		this._addLayers();

		if (seo === "letecka" || typeof seo !== "string") {
			if (!this._ophoto && !this._loadPromise) {
				// musime pockat na nacteni sceny
				this._loadPromise = import(
					/* webpackChunkName: "ophotoTimeline" */
					"~/ui/ophoto-timeline/index"
				);

				this._loadPromise.then(module => {
					module.initModule({ Scene, LayerManager: this, store });
					// eslint-disable-next-line new-cap
					this._ophoto = new module.default(layer => {
						this._zooms[layer.def] = layer.zoom;

						this.setMapset(layer);
					}, copyrights => {
						const layer = this.getLayer("ophoto");

						layer.setCopyright(copyrights);
						// aplikace zmen
						Scene.redraw();
					}, CONFIG.ophotoTimeline);
				});
			}

			this._loadPromise.then(() => {
				document.getElementById("scene").append(this._ophoto.container);
				this._ophoto.start();
			}, () => {});
		} else {
			this._removeOphotoTimeline();
			this._ophotoDateCheck = false;

			if (def.base === "ophoto") {
				this._ophotoDateCheck = true;
			} else {
				checkOphotoDate();
			}
		}

		this._mapRedraw({ longitude, latitude, zoom });
	}

	/**
	 * Vrati zadanou vrstvu. Nepridava do mapy ani nezapina.
	 */
	getLayer(def: string | {
		[key: string]: any;
	}) {
		const id = defToString(def);

		if (!(id in this._layers)) {
			let layer = null;
			const className = this._invertMapsets.includes(id) ? "invert" : "";

			if (typeof def === "string") { /* vyrobit z API konstanty */
				if (def in this._nameToId) {
					layer = Scene.createLayerFromMapset(this._nameToId[def]);
				} else {
					/* prozatim rekneme, ze je to nazev na mapserveru */
					const tileUrl = def.indexOf("http") === -1 ? `${CONFIG.mapserver.url}/${def}/` : def;

					layer = Scene.createLayerTile(null, tileUrl, {
						animTime: 0,
						//fadeTime: 0,
						prefetch: 0,
						alpha: true,
						...className ? { className } : {},
					});
				}

				const mapsetData = getMapsetData(def);

				if (mapsetData) {
					if (mapsetData.config.is_ophoto) {
						this._ophotoLayers.push(layer);
					}

					if (mapsetData.copyright) {
						layer.setCopyright(mapsetData.copyright);
					}
				}
			} else { /* vyrobit ze struktury */
				const obj = def;
				const opt: {
					alpha?: any;
					query?: string;
					fadeTime?: number;
					className?: string;
				} = {};

				if (obj.alpha) {
					opt.alpha = obj.alpha;
				}

				if (obj.query) {
					opt.query = obj.query;
				}

				if (typeof obj.fadeTime === "number") {
					opt.fadeTime = obj.fadeTime;
				}

				if (className) {
					opt.className = className;
				}

				layer = Scene.createLayerTile(null, obj.url, opt);
				layer.setCopyright(obj.copyright);

				if (obj.zoom) {
					this._zooms[layer.getId()] = obj.zoom;
				}
			}

			this._layers[id] = layer;
		}

		return this._layers[id];
	}

	_syncZoomRange(id: number) {
		let zoom: [number?, number?] = [];

		if (id in this._zooms) {
			zoom = this._zooms[id];
		} else if (Scene.CONFIG[id] && Scene.CONFIG[id].zoom) {
			zoom = Scene.CONFIG[id].zoom;
			const { longitude, latitude, zoom: stateZoom } = store.getState().map;

			if (this._syncOphotoZoom({ longitude, latitude, zoom: stateZoom })) {
				zoom = this._zooms[id];
			}
		}

		if (zoom.length) {
			Scene.setZoomRange(zoom[0], zoom[1]);
		}
	}

	_addLayer(def: string | {
		[key: string]: any;
	}, reused: {
		[key: string]: boolean;
	}, before?: boolean): any {
		const id = defToString(def);

		reused[id] = true;

		if (!(id in this._active)) {
			const layer = this.getLayer(def);

			this._active[id] = layer;
			Scene.addLayer(layer, before);
		}

		return this._active[id];
	}

	_mapRedraw({ longitude, latitude, zoom }: { longitude: number, latitude: number, zoom: number }) {
		const output = this._syncOphotoZoom({ longitude, latitude, zoom });

		this._ophotoDateCheck && checkOphotoDate([longitude, latitude], zoom);

		if (output) {
			this._syncZoomRange(output);
		}
	}

	_syncOphotoZoom({ longitude, latitude, zoom }: { longitude: number, latitude: number, zoom: number }): number {
		// checkeneme ophoto vrstvy
		let ophotoLayer = null;

		for (const id in this._active) {
			if (id in this._active) {
				const item = this._active[id];

				if (this._ophotoLayers.indexOf(item) !== -1) {
					ophotoLayer = item;
					break;
				}
			}
		}

		if (ophotoLayer) {
			const sel = ophotoLayer.getId();

			if (zoom > 18 && Scene.isOphotoAvailable(Scene.coordsFromWGS84(longitude, latitude))) {
				this._zooms[sel] = [2, 20];
			} else {
				this._zooms[sel] = Scene.CONFIG[sel].zoom;
			}

			return sel;
		}

		return 0;
	}

	_setMapsetTraffic({ longitude, latitude, zoom }: { longitude: number, latitude: number, zoom: number }) {
		// vlastni reseni dopravni mapy - smazeme predesle
		this._removeLayers({});

		const reused = {};
		// pridame vrstvy
		const base = this._addLayer(this._trafficConst.normalBase, reused, true);

		this._addLayer(this._trafficConst.traffic, reused);
		this._mapsetOverlays?.bumpLayers("middle");
		this._addLayer(this._trafficConst.normalUp, reused);

		/* nastavit rozsah zoomu, je-li */
		this._syncZoomRange(base.getId());

		/* aktivni vrstvy povolime */
		this._addLayers();

		// panorama
		this._mapsetOverlays?.bumpLayers("top");

		this._mapRedraw({ longitude, latitude, zoom });
	}

	_setMapsetWinter({ longitude, latitude, zoom }: { longitude: number, latitude: number, zoom: number }) {
		// vlastni reseni dopravni mapy - smazeme predesle
		this._removeLayers({});

		const reused = {};
		const base = this._addLayer(this._winterConst.normalBase, reused, true);

		this._addLayer(this._winterConst.winter, reused);
		this._mapsetOverlays?.bumpLayers("middle");
		this._addLayer(this._winterConst.normalUp, reused);

		/* nastavit rozsah zoomu, je-li */
		this._syncZoomRange(base.getId());

		/* aktivni vrstvy povolime */
		this._addLayers();

		// panorama
		this._mapsetOverlays?.bumpLayers("top");

		this._mapRedraw({ longitude, latitude, zoom });
	}

	_addExtraLayer(url: string, seo: string, zoom?: [number, number], addCopyright?: boolean) {
		// alpha = false - animace pri zoomu
		const opts: ILayerOptions = {
			animTime: 0,
			//fadeTime: 0,
			prefetch: 0,
			alpha: false,
		};

		if (url in RETINA) {
			opts.retinaUrl = `${CONFIG.mapserver.url}/${RETINA[url]}/`;
		}

		const layer = Scene.createLayerTile(null, `${CONFIG.mapserver.url}/${url}/`, opts);

		if (seo === "dopravni") {
			layer.setOptions({
				query: `${SceneLayerTile.getTileOptions().query}?s=0.2&dm=Luminosity`,
			});
		}

		if (zoom) {
			this._zooms[layer.getId()] = zoom;
		}
		if (addCopyright) {
			layer.setCopyright({
				"-": Scene.CONFIG[Scene.MAPSET.BASE].copyright["-"],
			});

			// copyright pro dopravni mapu z adminu
			const mapsetData = getMapsetData(seo);

			if (mapsetData && mapsetData.copyright) {
				layer.setCopyright(mapsetData.copyright);
			}
		}

		this._layers[url] = layer;
	}

	_addCustomLayer(url: string, name: string) {
		const opts: ILayerOptions = {
			animTime: 0,
			//fadeTime: 0,
			prefetch: 0,
			alpha: false,
		};

		const layer = Scene.createLayerTile(null, `${CONFIG.mapserver.url}/${url}/`, opts);

		this._layers[name] = layer;
	}

	_addLayers() {
		for (const id in this._active) {
			if (id in this._active) {
				//Scene.addLayer(this._active[id]).enable();
				this._active[id].enable();
			}
		}
	}

	_removeLayers(reused: {
		[key: string]: any;
	} = {}) {
		for (const id in this._active) {
			if (id in reused) {
				continue;
			}
			Scene.removeLayer(this._active[id].disable());
			delete this._active[id];
		}
	}

	_addAdditionalLayer(additionalId: string, reused: {
		[key: string]: any;
	} = {}) {
		let id = additionalId;

		// FIXME
		const matches = id.match(/%suffix/u);

		if (matches && matches.length) {
			const lang: string = CONFIG.defaultLang;
			const replaceValue = lang === "cs" || lang === "sk" ? "m" : "en";

			id = id.replace(matches[0], replaceValue);
		}

		this._addLayer(id, reused);
	}

	_removeOphotoTimeline() {
		this._ophoto?.stop();
		this._ophoto?.container.remove();
	}
}
